import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { rightSelector } from '../../../../../../modules/selectors/rights'
import { lineItemsSelector } from '../../../../../../modules/selectors/lineItems'

import { getUnitTypeLabel } from '../formatters/formatUnitType'
import { getIsBudgetAllocationType } from '../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/helpers'
import { formatUnitsToBudget } from '../formatters/transformLineItemToBE'
import { getInventoryItemId, getGamInventoryType } from '../helpers'

import {
  BILLING_EVENT,
  AD_INVENTORY
} from '../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/fields'
import { ALLOCATION } from '../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/InventoryAccessAndAllocationSection/SubAllocationTables/fields'

export const useUnitTypeLabel = () => {
  const right = useSelector(rightSelector)

  return useMemo(() => {
    return getUnitTypeLabel(right[BILLING_EVENT])
  }, [right])
}

export const useRemainingBudgetValidation = () => {
  const right = useSelector(rightSelector)
  const lineItems = useSelector(lineItemsSelector)

  const isBudgetAllocationType = getIsBudgetAllocationType(right)

  return useMemo(() => {
    const total = isBudgetAllocationType ? right.budget : right[ALLOCATION]

    const usedBudget = lineItems.reduce((sum, lineItem) => {
      const units = lineItem.primary_goal?.units || 0
      const isBudgetAllocationType = getIsBudgetAllocationType(right)
      const budget = isBudgetAllocationType ? formatUnitsToBudget(units, lineItem?.cost_per_unit?.micro_amount) : units

      return sum + budget
    }, 0)

    const remainingBudget = total - usedBudget

    return {
      total,
      usedBudget,
      remainingBudget
    }
  }, [right, lineItems, isBudgetAllocationType])
}

export const useSubAllocationRemainingBudget = () => {
  const right = useSelector(rightSelector)
  const lineItems = useSelector(lineItemsSelector)

  return useMemo(() => {
    if (!right?.is_sub_allocation || !right?.sub_allocation) {
      return {}
    }

    const inventoryBudgets = {}

    // Initialize with sub_allocation data
    right.sub_allocation.forEach(subAllocationItem => {
      const inventoryId = getInventoryItemId(subAllocationItem)
      inventoryBudgets[inventoryId] = {
        total: subAllocationItem.allocation,
        used: 0,
        remaining: subAllocationItem.allocation
      }
    })

    // Determine inventory type from right
    const inventoryType = getGamInventoryType(right[AD_INVENTORY])
    const isPlacementType = inventoryType === 'placements'
    const isBudgetAllocationType = getIsBudgetAllocationType(right)

    // Calculate used budget per inventory item from line items
    lineItems.forEach(lineItem => {
      const inventoryTargeting = lineItem.targeting?.inventory_targeting
      let targetedInventoryIds = []

      if (isPlacementType) {
        targetedInventoryIds = inventoryTargeting?.targeted_placement_ids || []
      } else {
        // For ad_units, extract ad_unit_id from the objects
        const targetedAdUnits = inventoryTargeting?.targeted_ad_units || []
        targetedInventoryIds = targetedAdUnits.map(adUnit => adUnit.ad_unit_id)
      }

      const units = lineItem.primary_goal?.units || 0
      const budget = isBudgetAllocationType ? formatUnitsToBudget(units, lineItem?.cost_per_unit?.micro_amount) : units

      // For sub-allocation, each line item targets only one inventory item
      if (targetedInventoryIds.length > 0) {
        const inventoryId = targetedInventoryIds[0] // Take the first (and only) inventory item
        if (inventoryBudgets[inventoryId]) {
          inventoryBudgets[inventoryId].used += budget
          inventoryBudgets[inventoryId].remaining =
            inventoryBudgets[inventoryId].total - inventoryBudgets[inventoryId].used
        }
      }
    })

    return inventoryBudgets
  }, [right, lineItems])
}
